/**
 * Iter 243 (Lot 4 polish) — Page unifiée "Mes pointages" pour chaque membre.
 *
 * Regroupe en 3 sections :
 * 1. Configuration WhatsApp (apikey CallMeBot personnelle + numéro)
 *    - Section affichée en compact si déjà configurée, expanded sinon
 * 2. Action de pointage (entrée / sortie)
 *    - Bouton selon l'état actuel (pointage ouvert ou non)
 *    - Géolocalisation + envoi OTP WhatsApp + saisie code → confirmation
 * 3. Historique de mes pointages (30 derniers jours)
 *    - Stats + liste réutilise l'endpoint /tracking/employees/{user_id}/summary
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth, isSalarieOfflineMode } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { useOfflineStatus } from "../lib/offline/offlineApi";
import { queueAll } from "../lib/offline/db";
import { QrScanner, parsePunchQr } from "../components/QrScanner";
import {
  Clock, MessageCircle, MapPin, CheckCircle2, AlertTriangle,
  Loader2, Play, Square, RefreshCw, ChevronDown, ChevronUp,
  Copy, ExternalLink, Send, Trash2, X, WifiOff, Hourglass, QrCode, FileDown,
} from "lucide-react";
import { toast } from "sonner";

const OFFLINE_PUNCH_URL = "/tracking/punch/offline";

function getPosition(timeout = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout, maximumAge: 60000 },
    );
  });
}

export default function MyPunchPage() {
  const { axiosAuth, user } = useAuth();
  // useMemo to keep `api` stable across renders (axiosAuth is keyed on token).
  // Without this, every render creates a new axios instance, causing useEffect
  // loops downstream → page flicker.
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [loading, setLoading] = useState(true);
  const [whatsappCfg, setWhatsappCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expandedWhatsapp, setExpandedWhatsapp] = useState(false);
  const [punchModal, setPunchModal] = useState(null); // null | { mode, otp_id, phone_masked, geofence_status }

  const userId = user?.user_id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        api.get("/integrations/callmebot/me"),
        userId ? api.get(`/tracking/employees/${userId}/summary?days=30`) : Promise.resolve({ data: null }),
      ]);
      setWhatsappCfg(w.data);
      setSummary(s.data);
      // Expand auto la config WhatsApp si pas configurée
      setExpandedWhatsapp(!w.data?.configured);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }

  const isWhatsappConfigured = whatsappCfg?.configured;
  const stats = summary?.stats || { total_punches: 0, total_hours: 0, days_worked: 0, in_progress: 0, needs_approval: 0 };
  const hasOpenPunch = stats.in_progress > 0;

  return (
    <div className="p-6 max-w-[900px] mx-auto space-y-5" data-testid="my-punch-page">
      <PageHeader
        icon={<Clock />}
        title="Mes pointages"
        description="Pointez votre journée de travail directement depuis l'application via WhatsApp."
        testId="my-punch-header"
      />

      {/* SECTION 1 : Config WhatsApp */}
      <WhatsAppConfigCard
        cfg={whatsappCfg}
        expanded={expandedWhatsapp}
        onToggle={() => setExpandedWhatsapp(v => !v)}
        onChanged={load}
      />

      {/* SECTION 2 : Action de pointage */}
      <PunchActionCard
        isWhatsappConfigured={isWhatsappConfigured}
        hasOpenPunch={hasOpenPunch}
        onPunchStarted={(modalData) => setPunchModal(modalData)}
        onSynced={load}
      />

      {/* SECTION 3 : Stats + Historique */}
      {summary && <PunchHistoryCard summary={summary} onRefresh={load} />}

      {/* Modal OTP */}
      {punchModal && (
        <OTPModal
          data={punchModal}
          onClose={() => setPunchModal(null)}
          onSuccess={() => { setPunchModal(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Section 1 : Configuration WhatsApp ────────────────────────────────

function WhatsAppConfigCard({ cfg, expanded, onToggle, onChanged }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [phone, setPhone] = useState(cfg?.phone || "");
  const [apikey, setApikey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { setPhone(cfg?.phone || ""); }, [cfg]);

  const isConfigured = !!cfg?.configured;
  const botNumber = cfg?.activation?.bot_number || "+34 644 97 54 14";
  const botMsg = cfg?.activation?.message_to_send || "I allow callmebot to send me messages";

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await api.put("/integrations/callmebot/me", {
        phone: phone.trim(),
        apikey: apikey.trim() || undefined,
      });
      setFeedback({ kind: "ok", text: "Configuration enregistrée ✓" });
      setApikey("");
      onChanged();
    } catch (e) {
      setFeedback({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      await api.post("/integrations/callmebot/test");
      setFeedback({ kind: "ok", text: "Message test envoyé. Vérifiez WhatsApp." });
    } catch (e) {
      setFeedback({ kind: "err", text: e.response?.data?.detail || "Échec" });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer votre configuration WhatsApp ?")) return;
    try {
      await api.delete("/integrations/callmebot/me");
      onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className={`bg-[#14101c] border rounded-xl ${isConfigured ? "border-emerald-500/30" : "border-amber-500/30"}`} data-testid="whatsapp-config-section">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02]" data-testid="whatsapp-toggle">
        <div className="flex items-center gap-3">
          <MessageCircle className={`w-5 h-5 ${isConfigured ? "text-emerald-400" : "text-amber-400"}`} />
          <div>
            <div className="text-sm font-bold text-white">
              WhatsApp {isConfigured ? "configuré" : "à configurer"}
            </div>
            <div className="text-xs text-slate-400">
              {isConfigured ? `${cfg?.phone || "—"} · prêt à recevoir vos codes de pointage` : "Activez WhatsApp pour pouvoir pointer"}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5">
          {!isConfigured && (
            <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3 mt-3 space-y-2">
              <h4 className="text-xs font-bold text-white">Procédure d'activation (3 étapes)</h4>
              <Step n={1} title="Ajoutez ce numéro à WhatsApp :">
                <CopyRow value={botNumber} />
              </Step>
              <Step n={2} title="Envoyez ce message au bot :">
                <CopyRow value={botMsg} />
              </Step>
              <Step n={3} title="Collez l'apikey reçue par WhatsApp ci-dessous puis enregistrez.">
                <a href="https://www.callmebot.com/blog/free-api-whatsapp-messages/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-cyan-400 hover:underline inline-flex items-center gap-1 mt-1">
                  Documentation CallMeBot <ExternalLink className="w-3 h-3" />
                </a>
              </Step>
            </div>
          )}

          <div className="space-y-2 pt-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Numéro WhatsApp (E.164)</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+33612345678" className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="whatsapp-phone-input" />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">
                API key CallMeBot {isConfigured && <span className="text-emerald-400 ml-1">— laissez vide pour conserver l'actuelle</span>}
              </label>
              <input type="text" value={apikey} onChange={e => setApikey(e.target.value)} placeholder={isConfigured ? "•••••••" : "1234567"} className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono" data-testid="whatsapp-apikey-input" />
            </div>
          </div>

          {feedback && (
            <div className={`text-[11px] p-2 rounded-md ${feedback.kind === "ok" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border border-rose-500/30"}`} data-testid="whatsapp-feedback">
              {feedback.text}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="whatsapp-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Enregistrer
            </Button>
            {isConfigured && (
              <>
                <Button onClick={test} disabled={testing} variant="outline" className="text-xs" data-testid="whatsapp-test-btn">
                  {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Envoyer un test
                </Button>
                <Button onClick={remove} variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/15 text-xs ml-auto">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section 2 : Action de pointage ────────────────────────────────────

function PunchActionCard({ isWhatsappConfigured, hasOpenPunch: serverOpenPunch, onPunchStarted, onSynced }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offlineOk, setOfflineOk] = useState(null);
  const [queued, setQueued] = useState([]);
  const [scanning, setScanning] = useState(false);
  const netStatus = useOfflineStatus();
  const salarieMode = isSalarieOfflineMode();

  useEffect(() => {
    queueAll().then((ops) => setQueued(ops.filter((o) => o.url === OFFLINE_PUNCH_URL))).catch(() => setQueued([]));
  }, [netStatus.pending]);
  const lastSyncRef = React.useRef(netStatus.lastSync);
  useEffect(() => {
    if (netStatus.lastSync && netStatus.lastSync !== lastSyncRef.current) { lastSyncRef.current = netStatus.lastSync; setOfflineOk(null); onSynced?.(); }
  }, [netStatus.lastSync, onSynced]);

  // Un pointage hors ligne en attente prime sur l'état serveur (entrée ⇒ en service, sortie ⇒ terminé)
  const hasOpenPunch = queued.length ? queued[queued.length - 1].body?.mode === "start" : serverOpenPunch;

  const punchOffline = async (mode, qr = null) => {
    setLoading(true); setGeoLoading(true); setError(null); setOfflineOk(null);
    try {
      const { lat, lng } = await getPosition();
      setGeoLoading(false);
      const r = await api.post(OFFLINE_PUNCH_URL, { mode, timestamp: new Date().toISOString(), lat, lng, ...(qr || {}) });
      const t = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
      const label = mode === "start" ? "Entrée" : "Sortie";
      if (qr) {
        setOfflineOk(r.queued
          ? `${label} par QR enregistrée à ${t}. Elle sera synchronisée au retour du réseau et validée automatiquement (présence sur site prouvée par le QR).`
          : `${label} par QR enregistrée à ${t} — validée automatiquement.`);
      } else {
        setOfflineOk(r.queued
          ? `${label} enregistrée hors ligne à ${t}. Elle sera synchronisée automatiquement au retour du réseau puis soumise à validation.`
          : `${label} enregistrée à ${t} — en attente de validation par votre responsable.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erreur lors du pointage hors ligne");
    } finally {
      setGeoLoading(false); setLoading(false);
    }
  };

  const requestPunch = async (mode) => {
    if (!isWhatsappConfigured) {
      setError("Configurez d'abord WhatsApp en haut de la page.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Géolocalisation
      let lat = null, lng = null;
      if (navigator.geolocation) {
        setGeoLoading(true);
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, enableHighAccuracy: true });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch (gerr) {
          console.warn("Géoloc refusée/timeout, on continue sans");
        } finally {
          setGeoLoading(false);
        }
      }
      const r = await api.post("/tracking/punch/start", { mode, lat, lng });
      onPunchStarted({
        mode,
        otp_id: r.data.otp_id,
        phone_masked: r.data.phone_masked,
        expires_in_minutes: r.data.expires_in_minutes,
        sms_status: r.data.sms_status,
        sms_error: r.data.sms_error,
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors du démarrage du pointage");
    } finally {
      setLoading(false);
    }
  };

  const onQrResult = React.useCallback((text) => {
    const parsed = parsePunchQr(text);
    setScanning(false);
    if (!parsed) { setError("QR non reconnu : scannez le QR code affiché sur le poste de pointage."); return; }
    punchOffline(hasOpenPunch ? "stop" : "start", parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenPunch]);

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="punch-action-section">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-cyan-400" />
        {hasOpenPunch ? "Vous êtes en service" : "Pointer une nouvelle session"}
      </h3>

      {scanning && <QrScanner onResult={onQrResult} onClose={() => setScanning(false)} hint="Le QR du poste change toutes les 20 minutes : l'heure du scan est enregistrée et vérifiée à la synchronisation." />}

      {salarieMode && (
        <Button
          onClick={() => setScanning(true)}
          disabled={loading}
          className="w-full mb-3 bg-[#f7c935] hover:bg-[#e6b825] text-[#0b0811] font-bold py-3"
          data-testid="punch-qr-btn"
        >
          <QrCode className="w-5 h-5 mr-2" /> {hasOpenPunch ? "Pointer ma sortie par QR du poste" : "Pointer mon entrée par QR du poste"}
        </Button>
      )}

      {!netStatus.online && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="punch-offline-panel">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-300 mb-2"><WifiOff className="w-4 h-4" /> Pas de réseau — pointage hors ligne</div>
          {salarieMode ? (
            <>
              <p className="text-[11px] text-slate-300 mb-3">L'heure et votre position (si disponible) sont enregistrées sur l'appareil, puis envoyées automatiquement au retour du réseau. Le pointage sera <b>validé par un responsable</b>.</p>
              <Button
                onClick={() => punchOffline(hasOpenPunch ? "stop" : "start")}
                disabled={loading}
                className={`w-full font-bold py-3 text-white ${hasOpenPunch ? "bg-rose-500 hover:bg-rose-600" : "bg-emerald-500 hover:bg-emerald-600"}`}
                data-testid="punch-offline-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : hasOpenPunch ? <Square className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                {geoLoading ? "Géolocalisation…" : hasOpenPunch ? "Pointer ma sortie hors ligne" : "Pointer mon entrée hors ligne"}
              </Button>
            </>
          ) : (
            <p className="text-[11px] text-slate-300">Le pointage hors ligne est disponible dans l'app <a href="/salarie/pointages" className="text-[#f7c935] underline">Blitzz Salarié</a>.</p>
          )}
        </div>
      )}

      {offlineOk && (
        <div className="mb-3 p-3 bg-emerald-500/15 border border-emerald-500/30 rounded-lg text-xs text-emerald-300 flex items-start gap-2" data-testid="punch-offline-ok">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {offlineOk}
        </div>
      )}

      {queued.length > 0 && (
        <div className="mb-3 space-y-1" data-testid="punch-offline-queue">
          {queued.map((q) => (
            <div key={q.id} className="flex items-center gap-2 text-[11px] text-amber-200 bg-[#0b0811] border border-amber-500/20 rounded-lg px-3 py-1.5" data-testid={`punch-queued-${q.id}`}>
              <Hourglass className="w-3.5 h-3.5" />
              <span className="font-bold">{q.body?.mode === "start" ? "Entrée" : "Sortie"}</span>
              <span className="text-slate-400">{new Date(q.body?.timestamp || q.created_at || Date.now()).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              <span className="ml-auto text-slate-500">en attente de synchro</span>
            </div>
          ))}
        </div>
      )}

      {hasOpenPunch ? (
        <Button
          onClick={() => requestPunch("stop")}
          disabled={loading || !isWhatsappConfigured || !netStatus.online}
          className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold py-3"
          data-testid="punch-stop-btn"
        >
          {loading || geoLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Square className="w-5 h-5 mr-2" />}
          {geoLoading ? "Géolocalisation…" : "Pointer ma sortie (FIN)"}
        </Button>
      ) : (
        <Button
          onClick={() => requestPunch("start")}
          disabled={loading || !isWhatsappConfigured || !netStatus.online}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3"
          data-testid="punch-start-btn"
        >
          {loading || geoLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
          {geoLoading ? "Géolocalisation…" : "Pointer mon entrée (DÉBUT)"}
        </Button>
      )}

      {!isWhatsappConfigured && (
        <p className="text-[11px] text-amber-300 mt-3 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          Vous devez d'abord configurer WhatsApp en haut de cette page pour pouvoir pointer.
        </p>
      )}

      {error && (
        <div className="mt-3 p-3 bg-rose-500/15 border border-rose-500/30 rounded-lg text-xs text-rose-300" data-testid="punch-error">
          {error}
        </div>
      )}

      <p className="text-[10px] text-slate-500 mt-3">
        Un code OTP à 6 chiffres vous sera envoyé sur WhatsApp pour confirmer votre pointage. Validité : 10 minutes.
      </p>
    </div>
  );
}

// ─── Section 3 : Historique ───────────────────────────────────────────

function PunchHistoryCard({ summary, onRefresh }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="punch-history-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-400" />
          Mes pointages (30 derniers jours)
        </h3>
        <button onClick={onRefresh} className="text-slate-400 hover:text-white" title="Rafraîchir">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <TimesheetDownload />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Stat label="Total heures" value={`${summary.stats.total_hours}h`} color="text-cyan-400" />
        <Stat label="Jours travaillés" value={summary.stats.days_worked} color="text-emerald-400" />
        <Stat label="Pointages" value={summary.stats.total_punches} color="text-white" />
        <Stat label="À valider" value={summary.stats.needs_approval} color={summary.stats.needs_approval > 0 ? "text-amber-400" : "text-slate-400"} />
      </div>

      {summary.punches.length === 0 ? (
        <p className="text-xs text-slate-500 italic text-center py-4">Aucun pointage sur les 30 derniers jours.</p>
      ) : (
        <div className="space-y-1 max-h-80 overflow-auto" data-testid="punches-list">
          {summary.punches.slice(0, 30).map(p => <PunchRow key={p.punch_id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function PunchRow({ p }) {
  const dur = p.duration_minutes != null ? p.duration_minutes : null;
  const start = p.start_at ? new Date(p.start_at) : null;
  const stop = p.stop_at ? new Date(p.stop_at) : null;
  const isAdmin = p.method === "admin_manual";
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 flex items-center gap-2 text-[11px]" data-testid={`punch-row-${p.punch_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-slate-300">{start?.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <span className="text-slate-600">→</span>
          {stop ? <span className="text-slate-300">{stop.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span> : <span className="text-emerald-400 font-bold">En cours</span>}
          {dur != null && <span className="text-cyan-400 font-bold">· {Math.floor(dur / 60)}h{String(dur % 60).padStart(2, "0")}</span>}
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-[10px]">
          {(p.geofence_status || p.start_geofence_status) && (
            <span className="flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" />
              {(() => { const g = p.geofence_status || p.start_geofence_status; return g === "inside" ? "Dans la zone" : g === "outside" ? "Hors zone" : g === "manual_admin" ? "Manuel admin" : g === "no_check" ? "Sans GPS" : g === "qr_verified" ? "QR du poste" : g; })()}
            </span>
          )}
          {isAdmin && p.admin_punched_by_name && <span className="text-purple-400">· par {p.admin_punched_by_name}</span>}
        </div>
      </div>
      {(p.qr_verified || p.stop_qr_verified) && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-bold uppercase inline-flex items-center gap-0.5" data-testid={`punch-qr-tag-${p.punch_id}`}><QrCode className="w-2.5 h-2.5" /> QR</span>}
      {(p.source === "offline" || p.stop_source === "offline") && !p.qr_verified && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold uppercase" data-testid={`punch-offline-tag-${p.punch_id}`}>Hors ligne</span>}
      {p.manager_approval_required && !p.manager_approval && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300 font-bold uppercase" data-testid={`punch-pending-tag-${p.punch_id}`}>À valider</span>}
      {p.manager_approval?.decision === "approved" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
      {p.manager_approval?.decision === "rejected" && <X className="w-3.5 h-3.5 text-rose-400" />}
    </div>
  );
}

// ─── Relevé mensuel PDF (Iter 340) ─────────────────────────────────────

function TimesheetDownload() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const current = new Date().toISOString().slice(0, 7);
  const [months, setMonths] = useState([current]);
  const [month, setMonth] = useState(current);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tracking/employees/me/timesheet-months").then((r) => {
      const list = Array.from(new Set([current, ...(r.data.months || [])])).sort().reverse();
      setMonths(list);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const download = async () => {
    if (!navigator.onLine) return toast.error("Le relevé PDF nécessite une connexion");
    setBusy(true);
    try {
      const r = await api.get(`/tracking/employees/me/timesheet.pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `releve-heures-${month}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(`Relevé ${label(month)} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 mb-4 bg-[#0b0811] border border-white/5 rounded-lg p-2.5 flex-wrap" data-testid="timesheet-download">
      <FileDown className="w-4 h-4 text-[#f7c935] flex-shrink-0" />
      <span className="text-[11px] text-slate-300 font-bold">Relevé mensuel PDF</span>
      <select value={month} onChange={(e) => setMonth(e.target.value)} className="bg-[#14101c] border border-white/10 rounded-lg text-xs text-white px-2 py-1.5 flex-1 min-w-[140px] capitalize" data-testid="timesheet-month-select">
        {months.map((m) => <option key={m} value={m} className="capitalize">{label(m)}</option>)}
      </select>
      <Button size="sm" onClick={download} disabled={busy} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-xs font-bold h-8" data-testid="timesheet-download-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Télécharger"}
      </Button>
    </div>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-lg p-2">
      <div className={`text-base font-black ${color}`}>{value}</div>
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-2 text-[11px] text-slate-300">
      <div className="w-5 h-5 rounded-full bg-[#f7c935] text-[#0b0811] font-black flex items-center justify-center flex-shrink-0 text-[10px]">{n}</div>
      <div className="flex-1">
        <div className="font-bold text-white">{title}</div>
        {children}
      </div>
    </div>
  );
}

function CopyRow({ value }) {
  return (
    <div className="flex items-center gap-2 bg-[#14101c] border border-white/5 rounded p-1.5 mt-1">
      <code className="flex-1 text-cyan-400 font-mono text-[11px]">{value}</code>
      <button onClick={() => navigator.clipboard.writeText(value)} className="text-slate-400 hover:text-white">
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Modal OTP ────────────────────────────────────────────────────────

function OTPModal({ data, onClose, onSuccess }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const verify = async () => {
    if (!code.trim() || code.length < 4) {
      setError("Code à 6 chiffres requis");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      await api.post("/tracking/punch/verify", { otp_id: data.otp_id, code: code.trim() });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || "Code incorrect");
    } finally {
      setVerifying(false);
    }
  };

  const isStart = data.mode === "start";

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="otp-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">
            Confirmer le pointage {isStart ? "DÉBUT" : "FIN"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {data.sms_status === "sent" ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4 flex items-start gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-emerald-200 font-bold">Code envoyé sur WhatsApp</p>
              <p className="text-[11px] text-emerald-300/80 mt-0.5">
                Au {data.phone_masked} · valide {data.expires_in_minutes} min
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200">{data.sms_error || "Échec d'envoi WhatsApp"}</p>
          </div>
        )}

        <label className="text-[11px] text-slate-400 mb-1 block">Code à 6 chiffres reçu sur WhatsApp</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-3 text-xl text-center text-white font-mono tracking-widest"
          data-testid="otp-input"
        />

        {error && (
          <div className="mt-3 p-2 bg-rose-500/15 border border-rose-500/30 rounded-md text-xs text-rose-300" data-testid="otp-error">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="ghost" onClick={onClose} className="text-xs">Annuler</Button>
          <Button onClick={verify} disabled={verifying || code.length < 4} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="otp-verify-btn">
            {verifying && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
            Confirmer le pointage
          </Button>
        </div>
      </div>
    </div>
  );
}
